import API from "./api";

/** Legacy single key — migrated per user and removed to avoid cross-account leakage. */
export const LEGACY_CART_KEY = "cart";

export function getUserIdFromStorage() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    if (!u) return null;
    const id = u._id ?? u.id;
    return id != null && String(id) !== "" ? String(id) : null;
  } catch {
    return null;
  }
}

export function cartStorageKeyForUserId(userId) {
  return userId ? `cc_cart_${userId}` : "cc_cart_guest";
}

export function getActiveCartStorageKey() {
  return cartStorageKeyForUserId(getUserIdFromStorage());
}

function readJsonArray(raw) {
  if (raw == null) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

/**
 * Read cart for the current session: logged-in users use a dedicated key; guests use `cc_cart_guest`.
 * Migrates legacy `cart` once into the appropriate key.
 */
export function readPersistedCart() {
  const uid = getUserIdFromStorage();
  const key = cartStorageKeyForUserId(uid);
  const scopedRaw = localStorage.getItem(key);
  if (scopedRaw !== null) {
    return readJsonArray(scopedRaw);
  }
  const legacy = readJsonArray(localStorage.getItem(LEGACY_CART_KEY));
  if (legacy.length) {
    localStorage.setItem(key, JSON.stringify(legacy));
    localStorage.removeItem(LEGACY_CART_KEY);
    return legacy;
  }
  return [];
}

export function writePersistedCart(cart) {
  const key = getActiveCartStorageKey();
  const next = Array.isArray(cart) ? cart : [];
  localStorage.setItem(key, JSON.stringify(next));
  localStorage.removeItem(LEGACY_CART_KEY);
  try {
    window.dispatchEvent(new CustomEvent("cc-cart-updated"));
  } catch {
    /* ignore */
  }
}

/** Remove legacy key after logout so the next session never inherits another account’s cart. */
export function persistLogoutCartCleanup() {
  localStorage.removeItem(LEGACY_CART_KEY);
}

export function clearActiveUserCartStorage() {
  const uid = getUserIdFromStorage();
  if (uid) localStorage.removeItem(cartStorageKeyForUserId(uid));
  localStorage.removeItem(LEGACY_CART_KEY);
  try {
    window.dispatchEvent(new CustomEvent("cc-cart-updated"));
  } catch {
    /* ignore */
  }
}

export async function clearServerAndLocalCart() {
  await API.put("/auth/cart-sync", { cart: [] }).catch(() => {});
  clearActiveUserCartStorage();
}

/**
 * After token + user are stored: merge server cart with this device’s cart for that user, then sync.
 * Prefers non-empty local cart over server so a session isn’t wiped by an empty server copy.
 */
export async function hydrateCartAfterAuth() {
  const uid = getUserIdFromStorage();
  if (!uid) return;
  const key = cartStorageKeyForUserId(uid);
  let localCart = readJsonArray(localStorage.getItem(key));
  if (!localCart.length) {
    localCart = readPersistedCart();
  }
  try {
    const { data } = await API.get("/auth/cart-sync");
    const serverCart = Array.isArray(data?.cart) ? data.cart : [];
    const merged = localCart.length ? localCart : serverCart;
    localStorage.setItem(key, JSON.stringify(merged));
    localStorage.removeItem(LEGACY_CART_KEY);
    if (merged.length) {
      await API.put("/auth/cart-sync", { cart: merged }).catch(() => {});
    }
  } catch {
    if (localCart.length) {
      localStorage.setItem(key, JSON.stringify(localCart));
    }
  }
  try {
    window.dispatchEvent(new CustomEvent("cc-cart-updated"));
  } catch {
    /* ignore */
  }
}

export async function syncCartToServerBeforeLogout() {
  const token = localStorage.getItem("token");
  if (!token) return;
  const cart = readPersistedCart();
  await API.put("/auth/cart-sync", { cart }).catch(() => {});
}
